import { useEffect } from "react";
import * as THREE from "three";
import { usePhysicsStore } from "../../stores/physicsStore";
import { logger } from "../../utils/logger";

export function usePhysicsPoses({ jointObjectsRef }) {
  // NOTE: Physics pose subscription is handled by Scene.jsx which has
  // more sophisticated toLocalTransform logic using convertPoseToLocal.
  // This hook provides normalize pivot groups and reset effects only.

  // Subscribe to physics running state for the normalization effect
  const isPhysicsRunning = usePhysicsStore((s) => s.isRunning);

  // Normalize any kinematics pivot groups when physics starts (to avoid double transforms)
  useEffect(() => {
    if (!isPhysicsRunning) return;
    try {
      const entries = Object.values(jointObjectsRef.current || {});
      entries.forEach((entry) => {
        const bodyGroup = entry && entry.bodyGroup;
        if (!bodyGroup) return;
        const pivot = bodyGroup.userData && bodyGroup.userData._pivotGroup;
        if (!pivot || !pivot.parent) return;
        const worldPos = new THREE.Vector3();
        const worldQuat = new THREE.Quaternion();
        bodyGroup.getWorldPosition(worldPos);
        bodyGroup.getWorldQuaternion(worldQuat);
        const newParent = pivot.parent;
        newParent.add(bodyGroup);
        try {
          pivot.parent.remove(pivot);
        } catch (error) {
          logger.warn("[usePhysicsPoses] Failed to remove pivot group from parent", error);
        }
        delete bodyGroup.userData._pivotGroup;
        delete bodyGroup.userData._pivotLocal;
        const localPos = worldPos.clone();
        if (typeof newParent.worldToLocal === "function") newParent.worldToLocal(localPos);
        const parentWorldQuat = new THREE.Quaternion();
        if (typeof newParent.getWorldQuaternion === "function")
          newParent.getWorldQuaternion(parentWorldQuat);
        const localQuat = parentWorldQuat.clone().invert().multiply(worldQuat);
        bodyGroup.position.copy(localPos);
        bodyGroup.quaternion.copy(localQuat);
      });
    } catch (e) {
      logger.warn("[Physics] Failed to normalize pivot groups:", e);
    }
  }, [isPhysicsRunning, jointObjectsRef]);
}
